import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'components/ui/sheet';
import { cn } from 'lib/utils';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { toast } from 'components/ui/use-toast';
import { useDeleteTemplate, useTemplate, type TemplateSummary } from 'modules/templates';

const PREVIEW_QUESTIONS = 5;

interface TemplatePreviewSheetProps {
  templateId: string | null;
  onClose: () => void;
  onUse: (id: string, title: string) => void;
  onAdapt: (id: string, title: string) => void;
  /** Whether the viewer may delete this (org) template; the API decides again. */
  canDelete: (template: TemplateSummary) => boolean;
}

/** Shows what the copy will contain before anything is created. */
export function TemplatePreviewSheet({ templateId, onClose, onUse, onAdapt, canDelete }: TemplatePreviewSheetProps) {
  const { t } = useTranslation();
  const { data: template, isLoading, isError } = useTemplate(templateId);
  const deleteTemplate = useDeleteTemplate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    if (!template) return;
    try {
      await deleteTemplate.mutateAsync(template.id);
      toast.success(t('templates.deleted'));
      setConfirmOpen(false);
      onClose();
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(t('templates.delete_failed'), { description: message });
    }
  };
  // Exam templates keep their content in sections; preview it the same way.
  const allQuestions = template ? (template.examSections?.flatMap((s) => s.questions) ?? template.questions) : [];
  const shown = allQuestions.slice(0, PREVIEW_QUESTIONS);
  const hidden = allQuestions.length - shown.length;

  return (
    <Sheet open={Boolean(templateId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" closeLabel={t('close')} className="flex w-full flex-col overflow-y-auto sm:max-w-lg">
        {isLoading || !template ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            {isError ? <p className="text-sm">{t('templates.error_title')}</p> : <Loader2 className="h-5 w-5 animate-spin" aria-label={t('loading')} />}
          </div>
        ) : (
          <>
            <SheetHeader className="pr-6 text-left">
              <SheetTitle>{template.title}</SheetTitle>
              <SheetDescription>{template.description}</SheetDescription>
            </SheetHeader>

            <dl className="mt-4 grid grid-cols-3 gap-2 rounded-lg border border-border p-3 text-center">
              <div>
                <dt className="text-[11px] text-muted-foreground">{t('templates.stat_questions')}</dt>
                <dd className="text-sm font-semibold tabular-nums">{template.questionCount}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-muted-foreground">{t('templates.stat_time')}</dt>
                <dd className="text-sm font-semibold tabular-nums">{t('templates.minutes', { count: template.timeLimitMinutes })}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-muted-foreground">{t('templates.stat_pass_mark')}</dt>
                <dd className="text-sm font-semibold tabular-nums">{template.passingScore}%</dd>
              </div>
            </dl>

            {template.subjects && template.subjects.length > 1 ? (
              <>
                <h4 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('templates.preview_subjects')}
                </h4>
                <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
                  {template.subjects.map((subject, index) => (
                    <li key={`${index}-${subject.name}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0 truncate text-foreground">{subject.name}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {t('templates.questions_count', { count: subject.questionCount })}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <h4 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('templates.preview_questions')}</h4>
            <ol className="mt-2 space-y-3">
              {shown.map((question, index) => (
                <li key={`${index}-${question.prompt}`} className="rounded-lg border border-border p-3">
                  <p className="text-sm text-foreground">
                    <span className="mr-1.5 tabular-nums text-muted-foreground">{index + 1}.</span>
                    {question.passageTitle ?? question.prompt}
                  </p>
                  {question.subQuestions?.length ? (
                    <ol className="mt-2 space-y-1 pl-5 text-xs text-muted-foreground">
                      {question.subQuestions.slice(0, PREVIEW_QUESTIONS).map((sub) => (
                        <li key={sub.id} className="list-decimal">{sub.questionText}</li>
                      ))}
                    </ol>
                  ) : null}
                  {question.options?.length ? (
                    <ul className="mt-2 space-y-1 pl-5 text-xs text-muted-foreground">
                      {question.options.map((option) => (
                        <li key={option.id} className="list-disc">{option.value}</li>
                      ))}
                    </ul>
                  ) : null}
                  {question.matchPairs?.length ? (
                    <ul className="mt-2 space-y-1 pl-5 text-xs text-muted-foreground">
                      {question.matchPairs.map((pair) => (
                        <li key={pair.key} className="list-disc">{pair.key}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ol>
            {hidden > 0 ? (
              <p className="mt-2 text-xs text-muted-foreground">{t('templates.preview_more', { count: hidden })}</p>
            ) : null}

            {/* Actions stack on phones and sit two-up from sm; long uz/ru labels wrap instead of overflowing.
                Delete gets its own quiet row so it never competes with the main actions. */}
            <div className="mt-auto space-y-2 border-t border-border pt-4">
              <div className={cn('grid gap-2', template.examStandard ? 'grid-cols-1' : 'sm:grid-cols-2')}>
                <Button
                  type="button"
                  className="h-auto min-h-9 w-full whitespace-normal py-2 sm:order-2"
                  onClick={() => onUse(template.id, template.title)}
                >
                  {t('templates.use')}
                </Button>
                {template.examStandard ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto min-h-9 w-full whitespace-normal py-2 sm:order-1"
                    onClick={() => onAdapt(template.id, template.title)}
                  >
                    <Sparkles className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                    {t('templates.adapt_action')}
                  </Button>
                )}
              </div>
              {template.scope === 'ORG' && canDelete(template) ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                  {t('delete')}
                </Button>
              ) : null}
            </div>
            <ConfirmDialog
              open={confirmOpen}
              onOpenChange={setConfirmOpen}
              title={t('templates.delete_title')}
              description={t('templates.delete_desc')}
              confirmLabel={t('delete')}
              cancelLabel={t('cancel')}
              destructive
              loading={deleteTemplate.isPending}
              onConfirm={handleDelete}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
